/**
 * Job Description API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type {
  JobDescription,
  JobDescriptionListResponse,
  JobDescriptionUpdateInput,
} from '../types';

interface JDListApiResponse {
  success: boolean;
  data: JobDescriptionListResponse;
  message: string;
}

interface JDApiResponse {
  success: boolean;
  data: JobDescription & { task_id?: string };
  message: string;
}

class JobDescriptionService {
  /**
   * List all Job Descriptions for a job.
   */
  async listJobDescriptions(
    workspaceId: string,
    idToken: string,
    jobId: string
  ): Promise<JDListApiResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<JDListApiResponse>(
      `/jobs/${jobId}/job-descriptions`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list job descriptions');
    }

    return response.data;
  }

  /**
   * Get a single Job Description by ID only (no jobId required).
   */
  async getJobDescriptionById(
    workspaceId: string,
    idToken: string,
    jdId: string,
  ): Promise<JobDescription> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<JDApiResponse>(
      `/job-descriptions/${jdId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get job description');
    }
    return response.data.data;
  }

  /**
   * List all Job Descriptions across all jobs in the workspace.
   * Optionally filter by search query, processing statuses, and job IDs.
   */
  async listAllJobDescriptions(
    workspaceId: string,
    idToken: string,
    params?: { q?: string; statuses?: string[]; jobIds?: string[] },
  ): Promise<JDListApiResponse> {
    const client = createApiClient(idToken, 'ats');
    const searchParams = new URLSearchParams();
    if (params?.q) searchParams.set('q', params.q);
    params?.statuses?.forEach((s) => searchParams.append('status', s));
    params?.jobIds?.forEach((id) => searchParams.append('job_id', id));
    const qs = searchParams.toString();
    const response = await client.get<JDListApiResponse>(
      `/job-descriptions${qs ? `?${qs}` : ''}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list job descriptions');
    }

    return response.data;
  }

  /**
   * Upload a JD file or paste raw content, then trigger AI parsing.
   * Exactly one of file or raw_content must be provided.
   */
  async uploadJobDescription(
    workspaceId: string,
    idToken: string,
    jobId: string,
    input: { name?: string; file?: File; raw_content?: string }
  ): Promise<JDApiResponse> {
    const client = createApiClient(idToken, 'ats');

    const formData = new FormData();
    if (input.name) formData.append('name', input.name);
    if (input.file) formData.append('file', input.file);
    if (input.raw_content) formData.append('raw_content', input.raw_content);

    const response = await client.post<JDApiResponse>(
      `/jobs/${jobId}/job-descriptions`,
      formData,
      {
        headers: {
          'X-Workspace-ID': workspaceId,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to upload job description');
    }

    return response.data;
  }

  /**
   * Get a single Job Description.
   */
  async getJobDescription(
    workspaceId: string,
    idToken: string,
    jobId: string,
    jdId: string
  ): Promise<JobDescription> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<JDApiResponse>(
      `/jobs/${jobId}/job-descriptions/${jdId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get job description');
    }

    return response.data.data;
  }

  /**
   * Update a Job Description.
   */
  async updateJobDescription(
    workspaceId: string,
    idToken: string,
    jobId: string,
    jdId: string,
    data: JobDescriptionUpdateInput
  ): Promise<JobDescription> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<JDApiResponse>(
      `/jobs/${jobId}/job-descriptions/${jdId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update job description');
    }

    return response.data.data;
  }

  /**
   * Get a short-lived signed URL for previewing the JD's attached file.
   */
  async getJDFileUrl(
    workspaceId: string,
    idToken: string,
    jobId: string,
    jdId: string
  ): Promise<{ url: string }> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<{ success: boolean; data: { url: string }; message: string }>(
      `/jobs/${jobId}/job-descriptions/${jdId}/file-url`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get file URL');
    }
    return response.data.data;
  }

  /**
   * Replace the file attached to a JD (resets parsing status to pending).
   */
  async uploadJDFile(
    workspaceId: string,
    idToken: string,
    jobId: string,
    jdId: string,
    file: File
  ): Promise<JobDescription> {
    const client = createApiClient(idToken, 'ats');
    const formData = new FormData();
    formData.append('file', file);
    const response = await client.post<JDApiResponse>(
      `/jobs/${jobId}/job-descriptions/${jdId}/upload-file`,
      formData,
      {
        headers: {
          'X-Workspace-ID': workspaceId,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to upload file');
    }
    return response.data.data;
  }

  /**
   * Re-trigger AI parsing for a Job Description.
   */
  async reparseJobDescription(
    workspaceId: string,
    idToken: string,
    jobId: string,
    jdId: string
  ): Promise<{ task_id: string }> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<{ success: boolean; data: { task_id: string }; message: string }>(
      `/jobs/${jobId}/job-descriptions/${jdId}/reparse`,
      {},
      { headers: { 'X-Workspace-ID': workspaceId } }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reparse job description');
    }

    return response.data.data;
  }

  /**
   * Delete a Job Description.
   */
  async deleteJobDescription(
    workspaceId: string,
    idToken: string,
    jobId: string,
    jdId: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/jobs/${jobId}/job-descriptions/${jdId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

export default new JobDescriptionService();
